import { Gauge } from 'lucide-react'
import { CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import type { LeadScore } from '@/types'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'

function getScoreColor(score: number): { color: string } {
  // Map score tiers to Grackle palette:
  // High (80+): evergreen (strong positive)
  // Medium-High (60-79): navy (solid, primary)
  // Warm (40-59): clay (warning-adjacent)
  // Low (20-39): pollen needs dark text, so use clay for consistency
  // Cold (0-19): raspberry (error)
  // All are .solid colors suitable for colored text/glyph on white (≥3:1).
  if (score >= 80) return { color: CATEGORICAL.evergreen.solid }
  if (score >= 60) return { color: CATEGORICAL.navy.solid }
  if (score >= 40) return { color: CATEGORICAL.clay.solid }
  if (score >= 20) return { color: CATEGORICAL.clay.solid }
  return { color: GRACKLE_COLORS.darkRaspberry }
}

interface LeadScoreCardProps {
  score: LeadScore | undefined
  isLoading?: boolean
}

export function LeadScoreCard({ score, isLoading }: LeadScoreCardProps) {
  if (isLoading) {
    return (
      <div className="text-center p-4" role="status">
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!score) return null

  const { color } = getScoreColor(score.score)
  const breakdownItems = [
    { key: 'engagement', label: 'Engagement' },
    { key: 'profile', label: 'Profile' },
    { key: 'source', label: 'Lead Source' },
    { key: 'status', label: 'Status' },
  ] as const

  return (
    <>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
        <CardTitle className="text-sm font-medium">Lead Score</CardTitle>
        <Gauge className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      </CardHeader>
      <CardContent className="p-3">
        <div
          className="text-xl font-bold leading-none"
          style={{ color }}
          aria-label={`Lead score: ${score.score} out of 100, ${score.label}`}
        >
          {score.score}
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button
              className="text-xs text-muted-foreground underline underline-offset-2 cursor-pointer hover:opacity-80 leading-tight"
              aria-label={`${score.label}. Click for score breakdown`}
            >
              {score.label} &mdash; Details
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="start">
            <p className="text-xs font-semibold mb-2">Score Breakdown</p>
            <div className="space-y-1.5">
              {breakdownItems.map(({ key, label }) => {
                const item = score.breakdown[key]
                return (
                  <div key={key} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{label}</span>
                    <span
                      className="font-medium"
                      title={item.detail}
                      aria-label={`${label}: ${item.score} of ${item.max}. ${item.detail}`}
                    >
                      {item.score}/{item.max}
                    </span>
                  </div>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </CardContent>
    </>
  )
}
